/**
 * ACE product info tabs (`blocks/ace-product-tabs.liquid`).
 *
 * The Liquid side renders one item per filled `_ace-product-tab` block —
 * metafield tabs with empty metafields render no item at all. Each item holds
 * a toggle button (the mobile accordion header) and a panel.
 *
 * Desktop (>= 750px): this element builds a tab bar from the panels'
 * `data-tab-heading` attributes and shows one panel at a time (ARIA tabs +
 * arrow-key navigation). The item toggles are hidden via CSS.
 *
 * Mobile (< 750px): the tab bar is hidden via CSS and the item toggles act as
 * a collapsed accordion (all items closed initially, multiple can be open).
 *
 * Both interaction models are wired unconditionally; CSS media queries decide
 * which controls are visible, so no resize handling is needed.
 */
class AceProductTabs extends HTMLElement {
  /** @type {HTMLElement | null} */
  nav = null;

  /** @type {HTMLElement[]} */
  items = [];

  /** @type {HTMLElement[]} */
  panels = [];

  /** @type {HTMLButtonElement[]} */
  tabs = [];

  connectedCallback() {
    this.nav = this.querySelector('.ace-product-tabs__nav');
    this.items = Array.from(this.querySelectorAll('.ace-product-tabs__item'));
    this.panels = this.items
      .map((item) => item.querySelector('.ace-product-tabs__panel'))
      .filter((panel) => panel instanceof HTMLElement);

    if (!this.nav) return;
    if (this.panels.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#buildTabs();
    this.#wireAccordion();
    this.#activate(0);

    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  #buildTabs() {
    if (!this.nav) return;
    this.nav.textContent = '';
    this.nav.setAttribute('role', 'tablist');
    this.tabs = this.panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'ace-product-tabs__tab';
      tab.id = `${panel.id}-tab`;
      tab.textContent = panel.dataset.tabHeading ?? '';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.addEventListener('click', () => this.#activate(index));
      tab.addEventListener('keydown', this.#onTabKeydown);
      this.nav?.appendChild(tab);
      panel.setAttribute('aria-labelledby', tab.id);
      return tab;
    });
    this.nav.hidden = false;
  }

  #wireAccordion() {
    this.items.forEach((item, index) => {
      const toggle = item.querySelector('.ace-product-tabs__item-toggle');
      toggle?.addEventListener('click', () => this.#toggleItem(index));
    });
  }

  /**
   * Mobile accordion: toggles the item at `index` open/closed.
   * @param {number} index
   * @param {boolean} [force] - open (true) / close (false) instead of toggling
   */
  #toggleItem(index, force) {
    const item = this.items[index];
    if (!item) return;
    const open = item.classList.toggle('is-open', force);
    item.querySelector('.ace-product-tabs__item-toggle')?.setAttribute('aria-expanded', String(open));
  }

  /**
   * Desktop tabs: shows the panel at `index` and updates tab states.
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, { focus = false } = {}) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      this.panels[i]?.classList.toggle('is-active', selected);
    });
    if (focus) this.tabs[index]?.focus();
  }

  /**
   * Roving-tabindex arrow-key navigation within the tab bar.
   * @param {KeyboardEvent} event
   */
  #onTabKeydown = (event) => {
    const current = this.tabs.findIndex((tab) => tab === event.currentTarget);
    if (current === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, { focus: true });
  };

  /**
   * Theme editor: selecting a tab block (or one of its nested blocks) in the
   * sidebar switches to that tab / opens that accordion item so the merchant
   * sees what they edit.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.ace-product-tabs__item');
    if (!item) return;
    const index = this.items.indexOf(/** @type {HTMLElement} */ (item));
    if (index < 0) return;
    this.#activate(index);
    this.#toggleItem(index, true);
  };
}

if (!customElements.get('ace-product-tabs')) {
  customElements.define('ace-product-tabs', AceProductTabs);
}
